import { useCallback, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import * as v from 'valibot';
import type { RootState } from '@redux/RootReducer';

import {
  useBranches,
  useCargoTypes,
  useCarrierServices,
  useCarriers,
  useCurrencies,
  useEquipmentTypes,
  useGetLane,
  useLinerTypes,
  usePackageTypes,
  usePricingTypes,
  useTariffTypes,
} from '@/modules/entities';
import { DEFAULT_MONTH_RANGE, getLocalTimeZone, toZonedDayIso } from '@/utils/date';
import { cleanNuqsURLParams } from '@/utils/url';

import { toFormOption } from '../data-provider';
import { type TariffsSearchFiltersFormValues, type TariffsSearchParams, tariffsSearchParamsSchema } from '../models';

const DEFAULT_TIME_ZONE = getLocalTimeZone();
const DEFAULT_EFFECTIVE_DATE = toZonedDayIso(DEFAULT_TIME_ZONE);
const DEFAULT_EXPIRATION_DATE = toZonedDayIso(DEFAULT_TIME_ZONE, DEFAULT_MONTH_RANGE, true);

const parsers = {
  branchKey: parseAsInteger,
  cargoType: parseAsString,
  carrierKey: parseAsInteger,
  currencyCode: parseAsString,
  customerLaneId: parseAsString,
  effectiveDate: parseAsString.withDefault(DEFAULT_EFFECTIVE_DATE),
  expirationDate: parseAsString.withDefault(DEFAULT_EXPIRATION_DATE),
  laneKey: parseAsInteger,
  linerType: parseAsString,
  packingCode: parseAsString,
  pricingType: parseAsString,
  serviceKey: parseAsInteger,
  sorting: parseAsString.withDefault('date-newest'),
  tariffKey: parseAsInteger,
  tariffName: parseAsString,
  tariffType: parseAsString,
  trailerKey: parseAsInteger,
};

export const getTariffsFormDefaultFormValues = (timeZone = getLocalTimeZone()): TariffsSearchFiltersFormValues => {
  return {
    branchKey: null,
    cargoType: null,
    carrierKey: null,
    currencyCode: null,
    customerLaneId: null,
    effectiveDate: toZonedDayIso(timeZone),
    expirationDate: toZonedDayIso(timeZone, DEFAULT_MONTH_RANGE, true),
    laneKey: null,
    linerType: null,
    packingCode: null,
    pricingType: null,
    serviceKey: null,
    tariffKey: null,
    tariffName: null,
    tariffType: null,
    trailerKey: null,
  };
};

export const useTariffSearchFilters = () => {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany, shallowEqual);
  const [rawFilters, setFilters] = useQueryStates(parsers, { history: 'replace' });

  const cleanedRawFilters = useMemo(() => {
    const result = v.safeParse(tariffsSearchParamsSchema, cleanNuqsURLParams(rawFilters));

    if (!result.success) {
      return {
        effectiveDate: parsers.effectiveDate.defaultValue,
        expirationDate: parsers.expirationDate.defaultValue,
      } as TariffsSearchParams;
    }
    return result.output;
  }, [rawFilters]);

  const customerKey = selectedCompany?.companyId ?? 0;

  const { data: branches } = useBranches({
    searchParams: {
      ids: cleanedRawFilters?.branchKey ? [String(cleanedRawFilters?.branchKey)] : [],
    },
  });
  const { data: pricingTypes } = usePricingTypes({
    searchParams: {
      ids: cleanedRawFilters?.pricingType ? [String(cleanedRawFilters?.pricingType)] : [],
    },
  });
  const { data: cargoTypes } = useCargoTypes({
    searchParams: {
      ids: cleanedRawFilters?.cargoType ? [String(cleanedRawFilters?.cargoType)] : [],
    },
  });
  const { data: carriers } = useCarriers({
    searchParams: {
      ids: cleanedRawFilters?.carrierKey ? [String(cleanedRawFilters?.carrierKey)] : [],
    },
  });
  const { data: currencies } = useCurrencies({
    searchParams: {
      ids: cleanedRawFilters?.currencyCode ? [String(cleanedRawFilters?.currencyCode)] : [],
    },
  });
  const { data: carrierServices } = useCarrierServices({
    searchParams: {
      ids: cleanedRawFilters?.serviceKey ? [String(cleanedRawFilters?.serviceKey)] : [],
    },
  });
  const { data: equipmentTypes } = useEquipmentTypes({
    searchParams: {
      ids: cleanedRawFilters?.trailerKey ? [String(cleanedRawFilters?.trailerKey)] : [],
    },
  });
  const { data: tariffsTypes } = useTariffTypes({
    searchParams: {
      ids: cleanedRawFilters?.tariffType ? [String(cleanedRawFilters?.tariffType)] : [],
    },
  });
  const { data: lane } = useGetLane(cleanedRawFilters?.laneKey || 0);
  const { data: linearTypes } = useLinerTypes({
    searchParams: {
      ids: cleanedRawFilters?.linerType ? [String(cleanedRawFilters?.linerType)] : [],
    },
  });
  const { data: packageTypes } = usePackageTypes({
    searchParams: {
      ids: cleanedRawFilters?.packingCode ? [String(cleanedRawFilters?.packingCode)] : [],
    },
  });

  // URL params -> form objects
  const formValues: TariffsSearchFiltersFormValues = useMemo(() => {
    return {
      branchKey: toFormOption(
        cleanedRawFilters?.branchKey,
        branches?.map?.[String(cleanedRawFilters?.branchKey)]?.branchDesc,
      ),
      cargoType: toFormOption(
        cleanedRawFilters?.cargoType,
        cargoTypes?.map?.[String(cleanedRawFilters?.cargoType)]?.label,
      ),
      carrierKey: toFormOption(
        cleanedRawFilters?.carrierKey,
        carriers?.map?.[String(cleanedRawFilters?.carrierKey)]?.carrierName,
      ),
      currencyCode: toFormOption(
        cleanedRawFilters?.currencyCode,
        currencies?.map?.[String(cleanedRawFilters?.currencyCode)]?.label,
      ),
      customerLaneId: cleanedRawFilters?.customerLaneId ?? null,
      effectiveDate: cleanedRawFilters?.effectiveDate || '',
      expirationDate: cleanedRawFilters?.expirationDate,
      laneKey: toFormOption(cleanedRawFilters?.laneKey, lane?.laneCode || ''),
      linerType: toFormOption(
        cleanedRawFilters?.linerType,
        linearTypes?.map?.[String(cleanedRawFilters?.linerType)]?.label,
      ),
      packingCode: toFormOption(
        cleanedRawFilters?.packingCode,
        packageTypes?.map?.[String(cleanedRawFilters?.packingCode)]?.label,
      ),
      pricingType: toFormOption(
        cleanedRawFilters?.pricingType,
        pricingTypes?.map?.[String(cleanedRawFilters?.pricingType)]?.label,
      ),
      serviceKey: toFormOption(
        cleanedRawFilters?.serviceKey,
        carrierServices?.map?.[String(cleanedRawFilters?.serviceKey)]?.label,
      ),
      tariffKey: cleanedRawFilters?.tariffKey ? String(cleanedRawFilters?.tariffKey) : undefined,
      tariffName: cleanedRawFilters?.tariffName ?? null,
      tariffType: toFormOption(
        cleanedRawFilters?.tariffType,
        tariffsTypes?.map?.[String(cleanedRawFilters?.tariffType)]?.label,
      ),
      trailerKey: toFormOption(
        cleanedRawFilters?.trailerKey,
        equipmentTypes?.map?.[String(cleanedRawFilters?.trailerKey)]?.label,
      ),
    };
  }, [
    cleanedRawFilters,
    branches,
    pricingTypes,
    cargoTypes,
    carriers,
    currencies,
    carrierServices,
    equipmentTypes,
    tariffsTypes,
    linearTypes?.map,
    packageTypes?.map,
    lane?.laneCode,
  ]);

  const updateFilters: typeof setFilters = (newFilters) => {
    return setFilters(
      Object.entries(newFilters as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, value]) => {
        if (value !== undefined && value !== '') {
          acc[key] = value;
        } else {
          acc[key] = null;
        }
        return acc;
      }, {}) as Parameters<typeof setFilters>[0],
    );
  };

  const clearFilters = useCallback(() => {
    return setFilters(
      Object.fromEntries(Object.keys(parsers).map((key) => [key, null])) as Parameters<typeof setFilters>[0],
    );
  }, [setFilters]);

  return { clearFilters, customerKey, formValues, setFilters: updateFilters, tariffsSearchParams: cleanedRawFilters };
};
